import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  SpaceBetween,
  FormField,
  Multiselect,
  MultiselectProps,
} from '@amzn/awsui-components-console';
import {
  type SearchFilters,
  TRANSLATION_STATUS_OPTIONS,
} from 'src/api/graphql/searchTypes';
import {
  LocaleType,
  useListAllLocalesQuery,
  useListNamespacesLazyQuery,
  useListStringGroupsLazyQuery,
  useListCxFamiliesLazyQuery,
} from 'src/generated/graphql';
import { useTextFilteredOptions } from 'src/common/hooks/useTextFilteredOptions';
import { SearchMode, SEARCH_MODE } from '../SearchPage';
import {
  EMPTY_ARRAY,
  MAX_SELECTION_COUNT,
  TRUNCATION_HINTS,
} from '../constants';
import {
  fetchAllCxFamilies,
  fetchAllNamespaces,
  fetchAllStringGroupsForNamespace,
  type CxFamilyItem,
} from '../searchFilterData';
import {
  buildStringGroupUnion,
  hasTruncatedStringGroups,
  pruneInvalidSelections,
  type CachedStringGroups,
} from '../searchFilterUtils';
import styles from './SearchFilterBar.module.scss';

interface SearchFilterBarProps {
  readonly mode: SearchMode;
  readonly filters: SearchFilters;
  readonly onFiltersChange: (
    update: SearchFilters | ((prev: SearchFilters) => SearchFilters),
  ) => void;
}

/**
 * Rebuilds selectedOptions from stored string values. Looks up the matching option
 * to preserve the human-readable label. Falls back to label===value for values
 * not found in the options array.
 */
const toSelectedOptions = (
  values: readonly string[] | null | undefined,
  optionsSource: MultiselectProps.Options,
): MultiselectProps.Option[] =>
  (values || []).map((v) => {
    const match = (optionsSource as MultiselectProps.Option[]).find(
      (opt) => opt.value === v,
    );
    return match
      ? { label: match.label, value: match.value }
      : { label: v, value: v };
  });

const toValues = (
  selected: readonly MultiselectProps.Option[],
): string[] | undefined => {
  const vals = selected.map((o) => o.value!).filter(Boolean);
  return vals.length > 0 ? vals : undefined;
};

/**
 * Disables unselected options when the selection count has reached the maximum.
 * Already-selected options remain enabled (so they can be deselected).
 * This provides a hard cap — the user cannot check a 101st checkbox.
 */
const applySelectionCap = (
  options: MultiselectProps.Options,
  selectedValues: readonly string[] | null | undefined,
  maxCount: number,
): MultiselectProps.Options => {
  const currentCount = (selectedValues || []).length;
  if (currentCount < maxCount) return options;

  const selectedSet = new Set(selectedValues || []);
  return (options as MultiselectProps.Option[]).map((opt) => {
    if (selectedSet.has(opt.value!)) {
      // Already selected — keep enabled so user can deselect
      return opt;
    }
    return {
      ...opt,
      disabled: true,
      disabledReason: `Maximum ${maxCount} selections reached`,
    };
  });
};

/** Constraint message shown when a filter has hit the selection cap. */
const selectionCapText = (
  selectedValues: readonly string[] | null | undefined,
): string | undefined =>
  (selectedValues || []).length >= MAX_SELECTION_COUNT
    ? `${MAX_SELECTION_COUNT} selected (maximum reached)`
    : undefined;

/**
 * Renders the constraint messages of a dropdown, each in its own element so the
 * selection-cap notice and the truncation hint can be shown side by side.
 */
const buildConstraintText = (
  ...messages: (string | undefined)[]
): React.ReactNode => {
  const present = messages.filter((m): m is string => Boolean(m));
  return present.length > 0
    ? present.map((message) => <div key={message}>{message}</div>)
    : undefined;
};

/**
 * Filter bar for search with dependent dropdowns and client-side local filtering.
 *
 * Business rules:
 * - Namespace and CX Family are always enabled (top-level independents)
 * - String Group is disabled until a Namespace is selected (scoped to namespace)
 * - CXID is disabled until a CX Family is selected (scoped to family)
 * - Translated Locale is always enabled in translation mode (options from listAllLocales)
 *
 * All dropdowns use fetch-all-on-mount with client-side local filtering:
 * - Namespace: listNamespaces (paginated fetch-all, client-side substring filtering)
 * - String Group: listStringGroups (paginated fetch-all per namespace, client-side substring filtering)
 * - CX Family: listCxFamilies (paginated fetch-all, client-side substring filtering)
 * - CXID: derived from listCxFamilies cxIds field for the selected family
 * - Translated Locale: listAllLocales(localeType: TARGET), cached (cache-first) — the
 *   set is CX-Family-derived and rarely changes; the backend returns it deduped and sorted
 *
 * Fetch-all pagination lives in `searchFilterData.ts`; when a fetch stops at the
 * page cap the affected dropdown shows a truncation hint so the partial option
 * list is visible in production, not just in the dev console.
 */
const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  mode,
  filters,
  onFiltersChange,
}) => {
  const isTranslation = mode === SEARCH_MODE.TRANSLATION;

  // --- Namespace (fetch-all on mount, client-side substring filtering) ---
  const [executeListNamespaces] = useListNamespacesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [namespaceOptions, setNamespaceOptions] = useState<
    MultiselectProps.Option[]
  >([]);
  const [namespaceLoading, setNamespaceLoading] = useState(true);
  const [namespaceTruncated, setNamespaceTruncated] = useState(false);
  const nsRequestIdRef = useRef(0);

  // Fetch all namespaces with pagination on mount
  useEffect(() => {
    setNamespaceLoading(true);
    const id = ++nsRequestIdRef.current;
    const isStale = () => id !== nsRequestIdRef.current;

    const loadNamespaces = async () => {
      const { items, truncated, aborted } = await fetchAllNamespaces(
        executeListNamespaces,
        isStale,
      );
      if (aborted || isStale()) return;

      setNamespaceOptions(
        items.map((ns) => ({ label: ns.namespaceId, value: ns.namespaceId })),
      );
      setNamespaceTruncated(truncated);
      setNamespaceLoading(false);
    };

    loadNamespaces().catch((error: unknown) => {
      if (!isStale()) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Failed to load namespaces', error);
        }
        setNamespaceLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    filteredOptions: filteredNamespaceOptions,
    handleFilteringChange: handleNamespaceFilter,
  } = useTextFilteredOptions(namespaceOptions);

  // --- String Group (fetch-all per namespace, client-side substring filtering) ---
  const [executeListStringGroups] = useListStringGroupsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [stringGroupOptions, setStringGroupOptions] = useState<
    MultiselectProps.Option[]
  >([]);
  const [stringGroupLoading, setStringGroupLoading] = useState(false);
  const [stringGroupTruncated, setStringGroupTruncated] = useState(false);
  const sgRequestIdRef = useRef(0);
  /**
   * Cache of fetched string groups per namespace to avoid refetching on add/remove
   * of selected namespaces. Each entry also records whether that namespace's
   * fetch was capped, so the truncation hint follows the current selection.
   *
   * Staleness tradeoff: string groups created after the initial fetch for a given
   * namespace won't appear in the dropdown until the component is remounted (e.g.
   * page navigation). This is acceptable for a filter dropdown whose purpose is to
   * narrow search results — missing a brand-new group doesn't block the user from
   * finding existing content.
   */
  const sgCacheRef = useRef<Map<string, CachedStringGroups>>(new Map());

  const selectedNamespaces = useMemo(
    () => filters.namespaceIds || EMPTY_ARRAY,
    [filters.namespaceIds],
  );
  const hasNamespaceSelected = selectedNamespaces.length > 0;

  const prevNamespacesRef = useRef<readonly string[]>(EMPTY_ARRAY);

  // Fetch string groups for all selected namespaces; cache per namespace.
  useEffect(() => {
    const prevNamespaces = prevNamespacesRef.current;
    prevNamespacesRef.current = selectedNamespaces;

    if (!hasNamespaceSelected) {
      ++sgRequestIdRef.current;
      setStringGroupOptions([]);
      setStringGroupTruncated(false);
      // Prune stringGroupIds when namespace changes to empty
      if (prevNamespaces.length > 0) {
        pruneInvalidSelections(onFiltersChange, 'stringGroupIds', []);
      }
      return;
    }

    /**
     * Publishes the union of the cached groups for the current selection and
     * prunes any selection the new options no longer offer.
     *
     * Only called once the data for every selected namespace is available —
     * either synchronously from cache or after all fetches complete.
     */
    const applyCachedGroups = () => {
      const unionOptions = buildStringGroupUnion(
        selectedNamespaces,
        sgCacheRef.current,
      );
      setStringGroupOptions(unionOptions);
      setStringGroupTruncated(
        hasTruncatedStringGroups(selectedNamespaces, sgCacheRef.current),
      );
      setStringGroupLoading(false);
      pruneInvalidSelections(onFiltersChange, 'stringGroupIds', unionOptions);
    };

    // Determine which namespaces need fetching (not in cache)
    const namespacesToFetch = selectedNamespaces.filter(
      (ns) => !sgCacheRef.current.has(ns),
    );

    // If all are cached, compute union immediately
    if (namespacesToFetch.length === 0) {
      ++sgRequestIdRef.current; // Invalidate any in-flight fetch from a previous run
      applyCachedGroups();
      return;
    }

    setStringGroupLoading(true);
    const id = ++sgRequestIdRef.current;
    const isStale = () => id !== sgRequestIdRef.current;

    const loadGroups = async () => {
      for (const ns of namespacesToFetch) {
        if (isStale()) return;
        const { items, truncated, aborted } =
          await fetchAllStringGroupsForNamespace(
            executeListStringGroups,
            ns,
            isStale,
          );
        if (aborted || isStale()) return;
        sgCacheRef.current.set(ns, { groups: items, truncated });
      }

      if (isStale()) return;
      applyCachedGroups();
    };

    loadGroups().catch((error: unknown) => {
      if (!isStale()) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Failed to load string groups', error);
        }
        setStringGroupLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNamespaces]);

  const {
    filteredOptions: filteredStringGroupOptions,
    handleFilteringChange: handleStringGroupFilter,
  } = useTextFilteredOptions(stringGroupOptions);

  // --- CX Family (fetch-all on mount from listCxFamilies, client-side substring filtering) ---
  const [executeListCxFamilies] = useListCxFamiliesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [allCxFamilies, setAllCxFamilies] = useState<CxFamilyItem[]>([]);
  const [cxFamilyOptions, setCxFamilyOptions] = useState<
    MultiselectProps.Option[]
  >([]);
  const [cxFamilyLoading, setCxFamilyLoading] = useState(true);
  const [cxFamilyTruncated, setCxFamilyTruncated] = useState(false);
  const cxFamilyRequestIdRef = useRef(0);

  // Fetch all CX families with pagination on mount
  useEffect(() => {
    setCxFamilyLoading(true);
    const id = ++cxFamilyRequestIdRef.current;
    const isStale = () => id !== cxFamilyRequestIdRef.current;

    const loadCxFamilies = async () => {
      const { items, truncated, aborted } = await fetchAllCxFamilies(
        executeListCxFamilies,
        isStale,
      );
      if (aborted || isStale()) return;

      setAllCxFamilies(items);
      setCxFamilyOptions(
        items.map((f) => ({ label: f.cxFamilyId, value: f.cxFamilyId })),
      );
      setCxFamilyTruncated(truncated);
      setCxFamilyLoading(false);
    };

    loadCxFamilies().catch((error: unknown) => {
      if (!isStale()) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Failed to load CX families', error);
        }
        setCxFamilyLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    filteredOptions: filteredCxFamilyOptions,
    handleFilteringChange: handleCxFamilyFilter,
  } = useTextFilteredOptions(cxFamilyOptions);

  // --- CXID (derived from the selected CX Family's cxIds in listCxFamilies response) ---
  const selectedFamilies = useMemo(
    () => filters.cxFamilies || EMPTY_ARRAY,
    [filters.cxFamilies],
  );
  const hasFamilySelected = selectedFamilies.length > 0;

  // Derive CXIDs from ALL selected CX Families (union, deduped, stable sorted)
  const cxIdOptions = useMemo<MultiselectProps.Option[]>(() => {
    if (!hasFamilySelected) return [];
    const cxIdSet = new Set<string>();
    for (const familyId of selectedFamilies) {
      const family = allCxFamilies.find((f) => f.cxFamilyId === familyId);
      if (family) {
        for (const cxId of family.cxIds) {
          cxIdSet.add(cxId);
        }
      }
    }
    return Array.from(cxIdSet)
      .sort()
      .map((cxId) => ({
        label: cxId,
        value: cxId,
      }));
  }, [hasFamilySelected, selectedFamilies, allCxFamilies]);

  // Prune CXID selections: when family selection changes, keep only cxIds still
  // present in the new options union; drop any that are no longer offered.
  // Guard: skip pruning while allCxFamilies is still loading — avoids wiping
  // cxIds on saved-search load before the family data has arrived.
  useEffect(() => {
    if (cxFamilyLoading) return; // data not yet available — don't prune prematurely
    pruneInvalidSelections(onFiltersChange, 'cxIds', cxIdOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cxIdOptions]);

  // --- Translated Locale (from listAllLocales with localeType: TARGET) ---
  /**
   * Fetch policy: `cache-first`.
   *
   * The target-locale set is derived from CX Family configuration and changes
   * rarely (a new locale appears only when a CX Family is reconfigured), so
   * paying for a network round trip on every mount of the filter bar is pure
   * latency on a REQUIRED filter. `cache-first` goes to the network only when
   * the normalized cache has no `listAllLocales({localeType: TARGET})` entry —
   * a fresh page load, or after the entry is evicted — and is served from cache
   * on every remount within the session.
   *
   * Beyond that, the only network request is the explicit `refetchLocales()`
   * below, which the dropdown's recovery link triggers. `refetch` bypasses the
   * fetch policy by design and always hits the network; it is gated on
   * `hasLocalesError` at the `onLoadItems` call site so an ordinary dropdown
   * open cannot trigger it (see the comment there).
   *
   * The other filter dropdowns use `network-only` because they page through
   * mutable, user-authored data (namespaces, string groups); locales are neither.
   *
   * `skip` keeps the request off the wire entirely in source mode, where this
   * filter is not rendered.
   */
  const {
    data: localesData,
    loading: localesLoading,
    error: localesError,
    refetch: refetchLocales,
  } = useListAllLocalesQuery({
    variables: { localeType: LocaleType.Target },
    fetchPolicy: 'cache-first',
    skip: !isTranslation,
  });

  /**
   * The backend returns the locales already deduplicated and SORTED across all
   * CX Families (`ListAllLocalesActivity`), so this is a straight 1:1 mapping to
   * dropdown options — no client-side sort, no client-side dedupe. Re-sorting
   * would only risk disagreeing with the server's ordering.
   */
  const translatedLocaleOptions = useMemo<MultiselectProps.Option[]>(
    () =>
      (localesData?.listAllLocales.locales ?? []).map((locale) => ({
        label: locale,
        value: locale,
      })),
    [localesData],
  );

  const hasLocalesError = Boolean(localesError);
  /** Loaded successfully but no CX Family declares any target locale. */
  const localesEmpty =
    !localesLoading && !hasLocalesError && translatedLocaleOptions.length === 0;

  const retryLoadLocales = () => {
    refetchLocales().catch((error: unknown) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Failed to reload translated locales', error);
      }
    });
  };

  const localeStatusType = localesLoading
    ? 'loading'
    : hasLocalesError
      ? 'error'
      : 'finished';

  const localePlaceholder = hasLocalesError
    ? 'Locales unavailable'
    : localesEmpty
      ? 'No locales available'
      : 'Filter by locale';

  const updateFilter = (
    key: keyof SearchFilters,
    selected: readonly MultiselectProps.Option[],
  ) => {
    const cappedKeys: (keyof SearchFilters)[] = [
      'namespaceIds',
      'stringGroupIds',
    ];
    const effectiveSelected =
      cappedKeys.includes(key) && selected.length > MAX_SELECTION_COUNT
        ? selected.slice(0, MAX_SELECTION_COUNT)
        : selected;
    onFiltersChange((prev) => ({
      ...prev,
      [key]: toValues(effectiveSelected),
    }));
  };

  return (
    <SpaceBetween direction="horizontal" size="s">
      {/*
       * Filter render order:
       * - Translation mode: Translated Locale (required), Namespace, String Group, CX Family, CXID
       * - Source mode: Namespace, String Group, CX Family, CXID, Status
       *
       * Translated Locale appears FIRST in translation mode because it is required
       * and is the primary scoping filter for translation searches. It is hidden
       * entirely in source mode (not supported by the backend for source searches),
       * and its query is skipped there so no request is made.
       */}

      {/* Translated Locale — translation-only, options from listAllLocales(TARGET) */}
      {isTranslation && (
        <FormField
          label={
            <>
              Translated Locale{' '}
              <span className={styles.requiredAsterisk}>*</span>
            </>
          }
          constraintText="Required for translation search"
          /*
           * `translatedLocales` is a REQUIRED filter, so a failed load blocks
           * translation search entirely. Surface it on the field itself rather
           * than only inside the dropdown, and never fall back to a hardcoded
           * list — a stale list that no longer matches CX-family data would
           * offer locales that silently match nothing.
           */
          errorText={
            hasLocalesError
              ? 'Could not load translated locales. Open the dropdown and select Retry.'
              : undefined
          }
        >
          <Multiselect
            selectedOptions={toSelectedOptions(
              filters.translatedLocales,
              translatedLocaleOptions,
            )}
            onChange={({ detail }) =>
              updateFilter('translatedLocales', detail.selectedOptions)
            }
            options={translatedLocaleOptions}
            /*
             * `filteringType="auto"` (AWSUI client-side substring match over the
             * option's own fields) rather than the `"manual"` + `onLoadItems`
             * substring filtering the Namespace / String Group / CX Family
             * dropdowns use: those page against the server, whereas the whole
             * target-locale set arrives in one `listAllLocales(TARGET)` response,
             * so there is nothing to fetch as the user types. Label === value
             * here, so matching on the label is sufficient — same reasoning as
             * the CXID dropdown below.
             *
             * NOT optional. The list is backend-derived from CX-family
             * configuration and can grow arbitrarily; without a filter input the
             * only way to reach a locale is to scroll the entire set.
             */
            filteringType="auto"
            /*
             * Deliberately NOT disabled in the error state: the recovery link
             * lives inside the dropdown, so disabling would strand the user with
             * no way to retry.
             */
            statusType={localeStatusType}
            loadingText="Loading locales..."
            errorText="Could not load translated locales."
            recoveryText="Retry"
            /*
             * Gated deliberately. Cloudscape fires `onLoadItems` on the FIRST
             * dropdown open of every mount, not only on recovery-link clicks:
             * `useSelect` wires `onOpen: () => fireLoadItems('')`, and
             * `useLoadItems` dedupes against a `prevFilteringText` ref that
             * starts `undefined`, so `undefined !== ''` lets the first open
             * through. Ungated, that (a) fired a redundant network `refetch()`
             * per mount, defeating the `cache-first` policy above, and (b) could
             * flip a fully populated dropdown into an error state if that
             * background refetch failed. Recovery is unaffected: the recovery
             * link is only rendered while `statusType === 'error'`, which is
             * exactly when `hasLocalesError` is true.
             *
             * Now that `filteringType="auto"` renders a filter input, Cloudscape
             * also fires `onLoadItems` on each filtering-text change
             * (`useSelect` → `fireLoadItems(event.detail.value)`). The same gate
             * covers that: typing in a healthy dropdown is a no-op. Typing while
             * already in the error state does retry, which is harmless and
             * desirable — the option list is empty then, so there is nothing to
             * filter and re-fetching is the only useful outcome.
             */
            onLoadItems={() => {
              if (hasLocalesError) retryLoadLocales();
            }}
            empty="No translated locales are configured for any CX family."
            placeholder={localePlaceholder}
            tokenLimit={2}
            ariaLabel="Translated Locale filter"
          />
        </FormField>
      )}

      {/* Namespace (fetched once, client-side substring filtering) */}
      <FormField
        label="Namespace"
        constraintText={buildConstraintText(
          selectionCapText(filters.namespaceIds),
          namespaceTruncated ? TRUNCATION_HINTS.NAMESPACES : undefined,
        )}
      >
        <Multiselect
          selectedOptions={toSelectedOptions(
            filters.namespaceIds,
            namespaceOptions,
          )}
          onChange={({ detail }) =>
            updateFilter('namespaceIds', detail.selectedOptions)
          }
          options={applySelectionCap(
            filteredNamespaceOptions,
            filters.namespaceIds,
            MAX_SELECTION_COUNT,
          )}
          filteringType="manual"
          onLoadItems={({ detail }) =>
            handleNamespaceFilter(detail.filteringText)
          }
          statusType={namespaceLoading ? 'loading' : 'finished'}
          loadingText="Loading namespaces..."
          placeholder="Filter by namespace"
          tokenLimit={2}
          ariaLabel="Namespace filter"
        />
      </FormField>

      {/* String Group (fetched per namespace, client-side substring filtering) */}
      <FormField
        label="String Group"
        constraintText={buildConstraintText(
          selectionCapText(filters.stringGroupIds),
          stringGroupTruncated ? TRUNCATION_HINTS.STRING_GROUPS : undefined,
        )}
      >
        <Multiselect
          selectedOptions={toSelectedOptions(
            filters.stringGroupIds,
            stringGroupOptions,
          )}
          onChange={({ detail }) =>
            updateFilter('stringGroupIds', detail.selectedOptions)
          }
          options={applySelectionCap(
            filteredStringGroupOptions,
            filters.stringGroupIds,
            MAX_SELECTION_COUNT,
          )}
          filteringType="manual"
          onLoadItems={({ detail }) =>
            handleStringGroupFilter(detail.filteringText)
          }
          statusType={stringGroupLoading ? 'loading' : 'finished'}
          loadingText="Loading string groups..."
          placeholder={
            hasNamespaceSelected
              ? 'Filter by string group'
              : 'Select a namespace first'
          }
          disabled={!hasNamespaceSelected}
          tokenLimit={2}
          ariaLabel="String Group filter"
        />
      </FormField>

      {/* CX Family (fetched once, client-side substring filtering) */}
      <FormField
        label="CX Family"
        constraintText={buildConstraintText(
          cxFamilyTruncated ? TRUNCATION_HINTS.CX_FAMILIES : undefined,
        )}
      >
        <Multiselect
          selectedOptions={toSelectedOptions(
            filters.cxFamilies,
            cxFamilyOptions,
          )}
          onChange={({ detail }) =>
            updateFilter('cxFamilies', detail.selectedOptions)
          }
          options={filteredCxFamilyOptions}
          filteringType="manual"
          onLoadItems={({ detail }) =>
            handleCxFamilyFilter(detail.filteringText)
          }
          statusType={cxFamilyLoading ? 'loading' : 'finished'}
          loadingText="Loading CX families..."
          placeholder="Filter by CX family"
          tokenLimit={2}
          ariaLabel="CX Family filter"
        />
      </FormField>

      {/* CXID (derived from selected CX Family's cxIds, disabled until CX Family selected) */}
      <FormField label="CXID">
        <Multiselect
          selectedOptions={toSelectedOptions(filters.cxIds, cxIdOptions)}
          onChange={({ detail }) =>
            updateFilter('cxIds', detail.selectedOptions)
          }
          options={cxIdOptions}
          /* filteringType="auto" (AWSUI client-side match over the option's own fields) instead of the server-paged "manual" filtering used by the Namespace / String Group / CX Family dropdowns: the CXID list arrives whole with the selected CX family, so there is nothing to fetch as the user types, and CXID label === value so matching on the label is sufficient. */
          filteringType="auto"
          statusType={cxFamilyLoading ? 'loading' : 'finished'}
          loadingText="Loading CXIDs..."
          placeholder={
            hasFamilySelected ? 'Filter by CXID' : 'Select a CX family first'
          }
          disabled={!hasFamilySelected}
          tokenLimit={2}
          ariaLabel="CXID filter"
        />
      </FormField>

      {/* Status filter — source-only (hidden in translation mode per backend contract) */}
      {!isTranslation && (
        <FormField label="Status">
          <Multiselect
            selectedOptions={toSelectedOptions(
              filters.statuses,
              TRANSLATION_STATUS_OPTIONS,
            )}
            onChange={({ detail }) =>
              updateFilter('statuses', detail.selectedOptions)
            }
            options={TRANSLATION_STATUS_OPTIONS}
            placeholder="Filter by status"
            tokenLimit={2}
            ariaLabel="Status filter"
          />
        </FormField>
      )}
    </SpaceBetween>
  );
};

export default SearchFilterBar;
